/**
 * React Query hooks for Budget Admin feature.
 * Provides hooks for fetching and managing user/role budgets.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchUserBudgets,
  fetchBudgetRoles,
  setUserBudgetOverride,
  removeUserBudgetOverride,
  updateRoleBudget,
  budgetAdminKeys,
} from '@/api/admin'
import type {
  ListUserBudgetsRequest,
  SetUserBudgetOverrideRequest,
  UpdateRoleBudgetRequest,
} from '@/types/budgetAdmin'

/**
 * Hook for fetching paginated user budgets list
 */
export function useUserBudgetList(params: ListUserBudgetsRequest = {}) {
  return useQuery({
    queryKey: budgetAdminKeys.userList(params),
    queryFn: () => fetchUserBudgets(params),
  })
}

/**
 * Hook for fetching budget roles
 */
export function useBudgetRoles() {
  return useQuery({
    queryKey: budgetAdminKeys.roles(),
    queryFn: fetchBudgetRoles,
  })
}

/**
 * Mutation hook for setting a user's budget override
 */
export function useSetUserBudgetOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      oid,
      request,
    }: {
      oid: string
      request: SetUserBudgetOverrideRequest
    }) => setUserBudgetOverride(oid, request),
    onSuccess: () => {
      // Invalidate budget queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: budgetAdminKeys.users() })
    },
  })
}

/**
 * Mutation hook for removing a user's budget override
 */
export function useRemoveUserBudgetOverride() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (oid: string) => removeUserBudgetOverride(oid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetAdminKeys.users() })
    },
  })
}

/**
 * Mutation hook for updating a role's budget
 */
export function useUpdateRoleBudget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      roleId,
      request,
    }: {
      roleId: string
      request: UpdateRoleBudgetRequest
    }) => updateRoleBudget(roleId, request),
    onSuccess: () => {
      // Invalidate both role and user budgets (users inherit from roles)
      queryClient.invalidateQueries({ queryKey: budgetAdminKeys.all })
    },
  })
}
